import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Reusable pagination generation function
export function generatePaginationItems(currentPage: number, totalPages: number) {
  if (!totalPages) return []
  
  const items = []
  
  // Always show first page
  items.push(1)
  
  if (currentPage > 3) {
    items.push("ellipsis")
  }
  
  // Show pages around current
  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
    if (i !== 1 && i !== totalPages) {
      items.push(i)
    }
  }
  
  if (currentPage < totalPages - 2) {
    items.push("ellipsis")
  }
  
  // Always show last page
  if (totalPages > 1) {
    items.push(totalPages)
  }
  
  return items
}

// Reusable date formatting function
export function formatDate(dateString: string) {
  const date = new Date(dateString)
  // Use a fixed timezone to avoid SSR/CSR hydration mismatches
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

// Reusable user display name function
export function getUserDisplayName(user: any) {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`
  }
  return user.userName || 'Unknown User'
}

// Theme-aware badge class names
export function getCreateModeBadgeClasses(createMode: boolean) {
  return createMode 
    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
    : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
}

export function getProfileCompletionBadgeClasses(isProfileComplete: boolean) {
  return isProfileComplete 
    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
}

// Convert HTML content to plain text for safe display
export function stripHtml(html?: string) {
  if (!html) return ''
  const withoutTags = html.replace(/<[^>]*>/g, ' ')
  return withoutTags.replace(/\s+/g, ' ').trim()
}

export function truncateWords(text: string, wordLimit: number, ellipsis = '…') {
  if (!text) return ''
  const words = text.trim().split(/\s+/)
  if (words.length <= wordLimit) {
    return text
  }
  return words.slice(0, wordLimit).join(' ') + ellipsis
}